/**
 * The moldings a cabinet run can carry — crown, stacking, light rail and the
 * counted trim pieces — and the one rule for whether a stored choice is used
 * (claude-docs/2026-10-30-venus-full-line-design.md, group 1).
 *
 * A line with a size catalog (Venus) offers its own SKUs, sized from the
 * catalog, each carrying the colours its price sheet prices it in. Every other
 * line — the generic custom, semi-custom and stock lines, and Tasoro until its
 * own accessories are imported — offers the generic set below, priced from
 * our figures.
 *
 * A stored choice is never rewritten when a run's line or colour changes
 * (colours change through many writers). It is judged here, at read time: the
 * profile is USED if the line sells it in every carrying cabinet's colour,
 * else LEFT OUT — never swapped for another profile (the user, 2026-10-30: "a
 * different color would look weird"). Changing the colour back brings it back.
 *
 * Pure.
 */
const { familyById, familyOf } = require('../cabinet-runs/families');
const { familySizes, unitsOf } = require('../cabinet-runs/vendorSizes');
const { findRunByIdOrAlias } = require('../cabinet-runs/runIds');
const {
  TRIM_MOLDING_NAMES,
  COUNTED_PIECE_NAMES,
} = require('../cabinet-runs/vendorUnitWords');

const IN = 0.0254;

const TRIM_KINDS = Object.freeze([
  'crown',
  'stacking',
  'light-rail',
  'piece',
  'valance',
  'end-panel',
]);

// The catalog kind each trim kind is sold as (`vendors/<line>/catalog.json`).
const CATALOG_KIND = Object.freeze({
  crown: 'crown-molding',
  stacking: 'stacking-molding',
  'light-rail': 'light-rail',
  piece: 'trim-molding',
  // the valance over the sink (group 3c), arched or raised-panel
  valance: 'valance',
  // the wainscot panel on an exposed cabinet side (group 3d)
  'end-panel': 'end-panel',
});

// The counted pieces' catalog kinds: the molding pieces (group 1), counted
// in 96" sticks, and the pieces sold each — the straight valance board,
// corbels and decorative posts (2026-10-31, group 3a). Not drawn.
const PIECE_KINDS = Object.freeze([
  'trim-molding',
  'valance-board',
  'corbel',
  'decorative-post',
]);
const EACH_PIECE_KINDS = new Set([
  'valance-board',
  'corbel',
  'decorative-post',
  // not counted pieces, but sold each and named by their face the same way
  'valance',
  'end-panel',
]);
const countOf = (pieceKind) =>
  EACH_PIECE_KINDS.has(pieceKind) ? 'each' : 'stick';

/**
 * The generic set, for every line with no molding SKUs of its own. Sizes in
 * inches (face height × projection); `costPerFt` is our COST a foot before the
 * materials markup, set near Venus's cheapest colour so a generic and a Venus
 * kitchen price alike (the design's "Figures to check").
 */
const GENERIC_TRIM = Object.freeze({
  crown: Object.freeze([
    {
      id: 'generic-crown-small',
      name: 'Crown Small',
      h: 2,
      d: 1.375,
      costPerFt: 8.4,
    },
    {
      id: 'generic-crown-medium',
      name: 'Crown Medium',
      h: 3.5,
      d: 2.5,
      costPerFt: 9.5,
    },
    {
      id: 'generic-crown-large',
      name: 'Crown Large',
      h: 5.25,
      d: 3.5,
      costPerFt: 15.4,
    },
  ]),
  stacking: Object.freeze([
    {
      id: 'generic-stacking',
      name: 'Stacking molding',
      h: 2.5,
      d: 0.75,
      costPerFt: 7.7,
    },
  ]),
  'light-rail': Object.freeze([
    {
      id: 'generic-light-rail',
      name: 'Light rail',
      h: 1.5,
      d: 0.75,
      costPerFt: 9.8,
    },
  ]),
  piece: Object.freeze([
    {
      id: 'generic-scribe',
      name: 'Scribe molding',
      h: 0.75,
      d: 0.25,
      costPerFt: 1.7,
    },
    {
      id: 'generic-shoe',
      name: 'Shoe molding',
      h: 0.75,
      d: 0.75,
      costPerFt: 3.1,
    },
    {
      id: 'generic-inside-corner',
      name: 'Inside corner molding',
      h: 0.75,
      d: 0.75,
      costPerFt: 4.9,
    },
    {
      id: 'generic-outside-corner',
      name: 'Outside corner molding',
      h: 0.875,
      d: 0.875,
      costPerFt: 2.8,
    },
    {
      id: 'generic-batten',
      name: 'Batten molding',
      h: 1.25,
      d: 0.25,
      costPerFt: 1.7,
    },
    // sold each (group 3a): our cost each, near Venus's cheapest colour
    {
      id: 'generic-straight-valance',
      name: 'Straight valance',
      pieceKind: 'valance-board',
      w: 96,
      h: 5.5,
      d: 0.75,
      costEach: 96,
    },
    {
      id: 'generic-corbel',
      name: 'Corbel',
      pieceKind: 'corbel',
      w: 4,
      h: 12,
      d: 6,
      costEach: 125,
    },
    {
      id: 'generic-post',
      name: 'Decorative post',
      pieceKind: 'decorative-post',
      shape: 'square',
      w: 3,
      h: 36,
      d: 3,
      costEach: 170,
    },
  ]),
  // the valance over the sink (group 3c): one each per style, cut to the gap
  // up to Venus's longest (54"), our cost near Venus's 54" in its cheapest
  // colour
  valance: Object.freeze([
    {
      id: 'generic-arched-valance',
      name: 'Arched valance',
      style: 'arched',
      w: 54,
      h: 5.5,
      d: 0.75,
      costEach: 40,
    },
    {
      id: 'generic-raised-panel-valance',
      name: 'Raised-panel valance',
      style: 'raised-panel',
      w: 54,
      h: 12,
      d: 0.75,
      costEach: 132,
    },
  ]),
  // the wainscot end panel (group 3d): Venus's four sizes, each ½" under the
  // side it dresses, our cost near Venus's cheapest colour (WP-BASE $91.84,
  // WP-W30 $41.44, W36 $45.92, W42 $60.48 in HF)
  'end-panel': Object.freeze([
    {
      id: 'generic-end-panel-base',
      name: 'Wainscot end panel',
      tier: 'base',
      w: 23.5,
      h: 29.5,
      d: 0.75,
      costEach: 92,
    },
    {
      id: 'generic-end-panel-wall-30',
      name: 'Wainscot end panel',
      tier: 'wall',
      w: 11.5,
      h: 29.5,
      d: 0.75,
      costEach: 42,
    },
    {
      id: 'generic-end-panel-wall-36',
      name: 'Wainscot end panel',
      tier: 'wall',
      w: 11.5,
      h: 35.5,
      d: 0.75,
      costEach: 46,
    },
    {
      id: 'generic-end-panel-wall-42',
      name: 'Wainscot end panel',
      tier: 'wall',
      w: 11.5,
      h: 41.5,
      d: 0.75,
      costEach: 61,
    },
  ]),
});

/** What each valance style is called. */
const VALANCE_NAMES = Object.freeze({
  arched: 'Arched valance',
  'raised-panel': 'Raised-panel valance',
});

const VENDOR_KIND_NAMES = Object.freeze({
  crown: 'Crown',
  stacking: 'Stacking molding',
  'light-rail': 'Light rail',
  'end-panel': 'Wainscot end panel',
});

// what a left-out choice is called in its reason
const KIND_WORD = Object.freeze({
  crown: 'crown',
  stacking: 'stacking molding',
  'light-rail': 'light rail',
  piece: 'piece',
  valance: 'valance',
  'end-panel': 'end panel',
});

const FRACTIONS = Object.freeze({
  0.125: '⅛',
  0.25: '¼',
  0.375: '⅜',
  0.5: '½',
  0.625: '⅝',
  0.75: '¾',
  0.875: '⅞',
});

/** Inches as a contractor writes them: 3½", 2⅝", ¾". */
function inchesText(n) {
  const whole = Math.floor(n);
  const frac = FRACTIONS[Math.round((n - whole) * 1000) / 1000] ?? '';
  return `${whole > 0 || !frac ? whole : ''}${frac}"`;
}

// a piece counted each is named by its face (corbel 5¼" × 13½"); a molding
// by its height
const sizeText = (pieceKind, u) =>
  countOf(pieceKind) === 'each'
    ? `${inchesText(u.w)} × ${inchesText(u.h)}`
    : inchesText(u.h);

// What a counted piece is and how it is counted — and a piece sold each, its
// size (it is drawn) and a post its section; nothing for the other kinds
const pieceFields = (kind, pieceKind, u) =>
  kind === 'piece'
    ? {
        pieceKind,
        count: countOf(pieceKind),
        ...(countOf(pieceKind) === 'each' ? { widthM: u.w * IN } : {}),
        ...(u.shape ? { shape: u.shape } : {}),
      }
    : {};

// A valance's style, its length and how short it may be trimmed (the
// catalog's "trimable to"; none stated, any length up to its own)
const valanceFields = (kind, u) =>
  kind === 'valance'
    ? {
        style: u.style,
        widthM: u.w * IN,
        trimToM: (u.trimTo ?? 0) * IN,
      }
    : {};

// A wainscot end panel's tier (a base's side or a wall cabinet's) and its
// face across the side
const endPanelFields = (kind, u) =>
  kind === 'end-panel' ? { tier: u.tier, widthM: u.w * IN } : {};

function genericProfile(kind, g) {
  // an end panel is named by its face; the rest by their height
  const pieceKind =
    g.pieceKind ?? (kind === 'end-panel' ? 'end-panel' : 'trim-molding');
  return Object.freeze({
    id: g.id,
    kind,
    label: `${g.name} ${sizeText(pieceKind, g)}`,
    heightM: g.h * IN,
    depthM: g.d * IN,
    sku: null,
    colours: null,
    costPerFt: g.costPerFt ?? null,
    ...(g.costEach != null ? { costEach: g.costEach } : {}),
    ...pieceFields(kind, pieceKind, g),
    ...valanceFields(kind, g),
    ...endPanelFields(kind, g),
  });
}

function vendorProfile(kind, u, pieceKind = CATALOG_KIND[kind]) {
  let name;
  if (kind === 'valance') name = VALANCE_NAMES[u.style];
  else if (kind !== 'piece') name = VENDOR_KIND_NAMES[kind];
  else name = TRIM_MOLDING_NAMES[u.sku] ?? COUNTED_PIECE_NAMES[u.sku] ?? u.sku;
  return Object.freeze({
    id: u.sku,
    kind,
    label: `${u.sku} — ${name} ${sizeText(pieceKind, u)}`,
    heightM: u.h * IN,
    depthM: u.d * IN,
    sku: u.sku,
    colours: Object.freeze([...(u.colours || [])]),
    costPerFt: null,
    ...pieceFields(kind, pieceKind, u),
    ...valanceFields(kind, u),
    ...endPanelFields(kind, u),
  });
}

/**
 * The profiles a line offers for one trim kind: its own SKUs when it has a
 * size catalog, else the generic set.
 *
 * @param {Object} family  a cabinet family (`families.familyById`)
 * @param {'crown'|'stacking'|'light-rail'|'piece'|'valance'|'end-panel'} kind
 * @returns {TrimProfile[]}
 */
function trimProfiles(family, kind) {
  if (familySizes(family)) {
    if (kind === 'piece')
      return PIECE_KINDS.flatMap((pk) =>
        unitsOf(family, pk).map((u) => vendorProfile(kind, u, pk))
      );
    return unitsOf(family, CATALOG_KIND[kind]).map((u) =>
      vendorProfile(kind, u)
    );
  }
  return (GENERIC_TRIM[kind] || []).map((g) => genericProfile(kind, g));
}

const colourName = (family, code) =>
  (family?.offering?.paint || []).find((p) => p.code === code)?.name || code;

/**
 * Whether a stored profile is used on cabinets in these colours — never a
 * different one in its place.
 *
 * @param {Object} family
 * @param {string} kind
 * @param {string} profileId  the stored choice
 * @param {(string|null)[]} colourCodes  each carrying cabinet's colour code
 *   (`vendorSizes.colourCodeFor`; null for a finish the line does not sell)
 * @returns {{ profile: TrimProfile } | { profile: null, reason: string }}
 */
function resolveTrimProfile(family, kind, profileId, colourCodes) {
  const offered = trimProfiles(family, kind);
  const profile = offered.find((p) => p.id === profileId);
  if (!profile) {
    // a generic choice reads as its name; a vendor's as its part number
    const generic = (GENERIC_TRIM[kind] || []).find((g) => g.id === profileId);
    const stored = generic ? genericProfile(kind, generic).label : profileId;
    return {
      profile: null,
      reason: `${stored} isn't a ${family?.name} piece — pick one`,
    };
  }
  if (!profile.colours) return { profile };
  const codes = colourCodes || [];
  if (codes.some((c) => c === null || c === undefined)) {
    return {
      profile: null,
      reason: `These cabinets wear a finish ${family.name} doesn't sell, so the ${KIND_WORD[kind]} is left out`,
    };
  }
  const missing = codes.find((c) => !profile.colours.includes(c));
  if (missing) {
    return {
      profile: null,
      reason: `${family.name} doesn't sell ${profile.sku} in ${colourName(family, missing)}, so it's left out`,
    };
  }
  return { profile };
}

/**
 * The line a cabinet's trim comes from: its run's, else the line recorded on
 * the cabinet, else the default line.
 */
function trimFamilyFor(room, obj) {
  const run = obj?.runId
    ? findRunByIdOrAlias(room?.cabinetRuns || [], obj.runId)
    : null;
  if (run) return familyOf(run);
  return familyById(obj?.runFamily) || familyOf(null);
}

module.exports = {
  TRIM_KINDS,
  VALANCE_NAMES,
  GENERIC_TRIM,
  inchesText,
  trimProfiles,
  resolveTrimProfile,
  trimFamilyFor,
};
